'use client';

import { forwardRef, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { CloudUpload } from 'lucide-react';

import { Text } from '@src/design-system/text';
import { cn } from '@src/design-system/helpers';
import FilePreview from './file-preview';

interface FileUploadProps {
  name: string;
  onChange: (file: File | null) => void;
  allowedFormats?: string;
  maxSize?: string;
  multipleFile?: boolean;
  className?: string;
}

const AvatarUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      name,
      onChange,
      allowedFormats = '.jpg, .png, .svg, .zip, .pdf',
      maxSize = 'MAX. 800x400px',
      multipleFile = false,
      className,
    },
    ref
  ) => {
    const [file, setFile] = useState<File | null>(null);

    const dropzoneAccept = useMemo(() => {
      const formats = allowedFormats.split(',').map((format) => format.trim());
      const acceptObj: { [key: string]: string[] } = {};

      formats.forEach((format) => {
        if (format === '.jpg') acceptObj['image/jpeg'] = ['.jpg'];
        if (format === '.png') acceptObj['image/png'] = ['.png'];
        if (format === '.svg') acceptObj['image/svg+xml'] = ['.svg'];
        if (format === '.zip') acceptObj['application/zip'] = ['.zip'];
        if (format === '.pdf') acceptObj['application/pdf'] = ['.pdf'];
      });

      return acceptObj;
    }, [allowedFormats]);

    const onDrop = (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const uploadedFile = acceptedFiles[0];

        setFile(uploadedFile);
        onChange(uploadedFile);
      }
    };

    const handleDelete = () => {
      setFile(null);
      onChange(null);
    };

    const handleRename = (newName: string) => {
      if (file) {
        const renamedFile = new File([file], newName, {
          type: file.type,
          lastModified: file.lastModified,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }) as File | any;

        renamedFile.path = `./${newName}`;
        renamedFile.relativePath = `./${newName}`;

        setFile(renamedFile);

        onChange(renamedFile);
      }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      multiple: multipleFile,
      accept: dropzoneAccept,
    });

    return (
      <>
        {file && (
          <FilePreview
            file={file}
            onDelete={handleDelete}
            onRename={handleRename}
          />
        )}

        <div
          {...getRootProps()}
          ref={ref}
          className={cn(
            'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all bg-primary-30',
            isDragActive ? 'border-purple-500' : 'border-primary-200',
            file ? 'hidden' : '',
            className
          )}
        >
          <input
            {...getInputProps()}
            name={name}
            className='hidden'
          />
          <CloudUpload
            className='mx-auto text-gray-800'
            size={40}
          />
          <Text className='text-gray-800 font-semibold py-2'>
            Click to upload{' '}
            <Text
              variant='span'
              className='font-normal'
            >
              or drag and drop
            </Text>
          </Text>
          <Text className='text-xs text-gray-500'>
            Only support {allowedFormats} files (MAX. {maxSize})
          </Text>
        </div>
      </>
    );
  }
);

AvatarUpload.displayName = 'AvatarUpload';

export default AvatarUpload;
